import { supabase } from "../../supabaseClient";
import logger from "../../utils/logger";

/**
 * Сервис для работы с уведомлениями
 */
class NotificationService {
  constructor() {
    this.realtimeErrorLogged = false;
    this.cacheKey = "mgfood_notifications_cache";
    this.maxCacheItems = 50;
  }

  // Типы уведомлений, которые нельзя отключить (всегда показываем)
  static ALWAYS_ON_TYPES = ["menu_activated", "negative_balance"];

  /**
   * Загружает типы уведомлений, которые пользователь выключил
   * (user_notification_prefs.enabled = false). Обязательные не учитываются.
   * @param {string} userId — auth.uid()
   * @returns {Promise<Set<string>>} множество выключенных типов
   */
  async _getDisabledPrefTypes(userId) {
    if (!userId) return new Set();
    try {
      const { data, error } = await supabase
        .from("user_notification_prefs")
        .select("type")
        .eq("user_id", userId)
        .eq("enabled", false);
      if (error) throw error;
      const disabled = new Set((data || []).map((r) => r.type));
      // Обязательные никогда не скрываем
      NotificationService.ALWAYS_ON_TYPES.forEach((t) => disabled.delete(t));
      return disabled;
    } catch (e) {
      logger.error("🔔 Error loading disabled pref types:", e);
      return new Set();
    }
  }

  /**
   * Фильтрует список уведомлений, убирая выключенные пользователем типы.
   * @param {Array} notifications
   * @param {Set<string>} disabledTypes
   */
  _filterByPrefs(notifications, disabledTypes) {
    if (!disabledTypes || disabledTypes.size === 0) return notifications;
    return (notifications || []).filter((n) => !disabledTypes.has(n.type));
  }

  _normalizeClassValue(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/ё/g, "е")
      .replace(/класс/g, "")
      .replace(/[\s\-_.]/g, "")
      .trim();
  }

  _isTutorAssignedToClass(tutor, className) {
    const targetClass = this._normalizeClassValue(className);

    if (!targetClass || !tutor) {
      return false;
    }

    if (this._normalizeClassValue(tutor.class) === targetClass) {
      return true;
    }

    if (Array.isArray(tutor.classes)) {
      return tutor.classes.some(
        (assignedClass) =>
          this._normalizeClassValue(assignedClass) === targetClass,
      );
    }

    return false;
  }

  // --- Caching Logic ---
  _saveToCache(notifications) {
    try {
      const data = notifications.slice(0, this.maxCacheItems);
      localStorage.setItem(
        this.cacheKey,
        JSON.stringify({
          timestamp: Date.now(),
          items: data,
        }),
      );
    } catch (e) {
      logger.error("🔔 Cache save error:", e);
    }
  }

  _getFromCache() {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (!cached) return null;
      return JSON.parse(cached).items;
    } catch (e) {
      return null;
    }
  }

  async _getUserRole(user) {
    try {
      const { data } = await supabase
        .from("users_unified")
        .select("role")
        .eq("email", user.email)
        .eq("is_active", true)
        .maybeSingle();
      return data?.role || null;
    } catch {
      return null;
    }
  }

  async _getTutorByClass(className) {
    try {
      const { data, error } = await supabase
        .from("users_unified")
        .select("id, class, classes")
        .eq("role", "tutor")
        .eq("is_active", true);

      if (error) {
        throw error;
      }

      return (
        (data || []).find((tutor) =>
          this._isTutorAssignedToClass(tutor, className),
        ) || null
      );
    } catch (error) {
      logger.warn("🔔 Tutor lookup failed for notification", {
        className,
        error: error?.message,
        code: error?.code,
      });
      return null;
    }
  }

  async _getSystemAdmins() {
    try {
      const { data, error } = await supabase
        .from("users_unified")
        .select("id")
        .in("role", ["admin", "superadmin", "megaadmin"])
        .eq("is_active", true);

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      logger.warn("🔔 Admin lookup failed for notification", {
        error: error?.message,
        code: error?.code,
      });
      return [];
    }
  }

  async getNotifications(filters = {}, page = 1, limit = 20, routeContext = {}) {
    try {
      // 0. Fallback to cache if first page and no filters
      const isFirstPageNoFilters =
        page === 1 &&
        !filters.type &&
        (!filters.is_read || filters.is_read === "");

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user)
        return {
          notifications: [],
          pagination: { page, limit, total: 0, totalPages: 0 },
        };

      let query = supabase
        .from("notifications")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: false });

      // Получаем реальную роль из БД (не из app_metadata)
      const userRole = await this._getUserRole(user);
      const isStudentRole = userRole === "student";

      if (isStudentRole) {
        const { data: studentData } = await supabase
          .from("users_unified")
          .select("id")
          .eq("email", user.email)
          .eq("role", "student")
          .maybeSingle();

        if (studentData) {
          // Студент видит ТОЛЬКО свои (user_id = null)
          query = query.eq("student_id", studentData.id).is("user_id", null);
        } else {
          return {
            notifications: [],
            pagination: { page, limit, total: 0, totalPages: 0 },
          };
        }
      } else {
        // Админы и тьюторы видят ТОЛЬКО адресованные лично им
        // Это исключает дубликаты от студентов и других админов
        query = query.eq("user_id", user.id);
      }

      if (filters.type) query = query.eq("type", filters.type);
      if (filters.is_read !== undefined && filters.is_read !== "")
        query = query.eq(
          "is_read",
          filters.is_read === "true" || filters.is_read === true,
        );
      if (filters.date_from) query = query.gte("created_at", filters.date_from);
      if (filters.date_to) query = query.lte("created_at", filters.date_to);

      const from = (page - 1) * limit;
      const to = from + limit - 1;
      const { data, error, count } = await query.range(from, to);

      if (error) throw error;

      // Фильтруем по предпочтениям пользователя (скрываем выключенные типы)
      const disabledTypes = await this._getDisabledPrefTypes(user.id);
      const filteredData = this._filterByPrefs(data, disabledTypes);

      // Обогащаем данные уведомлений информацией об ученике (имя, класс)
      const enrichedNotifications = await Promise.all(
        (filteredData || []).map(async (n) => {
          if (n.student_id) {
            const { data: student } = await supabase
              .from("users_unified")
              .select("full_name, class")
              .eq("id", n.student_id)
              .maybeSingle();

            if (student) {
              n.student_name = student.full_name;
              n.student_class = student.class;
            }
          }
          return n;
        }),
      );

      if (isFirstPageNoFilters) {
        this._saveToCache(enrichedNotifications);
      }

      return {
        notifications: enrichedNotifications,
        pagination: {
          page,
          limit,
          total: count || 0,
          totalPages: Math.ceil((count || 0) / limit),
        },
      };
    } catch (error) {
      // Structured error logging with metadata for diagnostics
      const authState = await this._getAuthState();
      logger.error("🔔 Notification fetch failed", {
        route: routeContext.route || window?.location?.pathname || 'unknown',
        authState,
        supabaseError: {
          code: error?.code,
          status: error?.status,
          message: error?.message,
          hint: error?.hint,
        },
        context: {
          filters,
          page,
          limit,
          timestamp: new Date().toISOString(),
        },
        action: 'Check user permissions and Supabase connection',
      });

      // Try cache on error
      const cached = this._getFromCache();
      if (cached && page === 1) {
        return {
          notifications: cached,
          pagination: {
            page: 1,
            limit: 20,
            total: cached.length,
            totalPages: 1,
          },
          from_cache: true,
        };
      }
      throw error;
    }
  }

  async _getAuthState() {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) return { authenticated: false, error: error.message };
      if (!user) return { authenticated: false };
      return {
        authenticated: true,
        userId: user.id,
        email: user.email,
        role: await this._getUserRole(user),
      };
    } catch (e) {
      return { authenticated: false, error: e.message };
    }
  }

  async getUnreadCount() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return 0;

      let query = supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("is_read", false);

      const userRole = await this._getUserRole(user);

      if (userRole === "student") {
        const { data: studentData } = await supabase
          .from("users_unified")
          .select("id")
          .eq("email", user.email)
          .eq("role", "student")
          .maybeSingle();
        if (studentData) {
          query = query.eq("student_id", studentData.id).is("user_id", null);
        } else return 0;
      } else {
        // Для всех остальных (админы, тьюторы) строго по user_id
        query = query.eq("user_id", user.id);
      }

      // Исключаем выключенные пользователем типы (на уровне запроса)
      const disabledTypes = await this._getDisabledPrefTypes(user.id);
      if (disabledTypes.size > 0) {
        query = query.not("type", "in", `(${[...disabledTypes].join(",")})`);
      }

      const { count, error } = await query;
      return error ? 0 : count || 0;
    } catch (error) {
      return 0;
    }
  }

  async markAsRead(notificationId) {
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("id", notificationId);
    return !error;
  }

  async toggleNotificationStatus(notificationId, currentStatus) {
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: !currentStatus })
      .eq("id", notificationId);
    return !error;
  }

  async markAllAsRead() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return 0;

      let query = supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("is_read", false);

      const userRole = await this._getUserRole(user);

      if (userRole === "student") {
        const { data: studentData } = await supabase
          .from("users_unified")
          .select("id")
          .eq("email", user.email)
          .eq("role", "student")
          .maybeSingle();
        if (studentData) {
          query = query.eq("student_id", studentData.id).is("user_id", null);
        } else return 0;
      } else {
        query = query.eq("user_id", user.id);
      }

      const { error, count } = await query.select("*", {
        count: "exact",
      });

      if (error) {
        logger.error("🔔 Error in markAllAsRead:", error);
        throw error;
      }

      return count || 0;
    } catch (error) {
      logger.error("🔔 Catch in markAllAsRead:", error);
      return 0;
    }
  }

  async deleteNotification(notificationId) {
    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("id", notificationId);
    return !error;
  }

  async bulkMarkAsRead(notificationIds) {
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .in("id", notificationIds);
    return !error;
  }

  async bulkDelete(notificationIds) {
    const { error } = await supabase
      .from("notifications")
      .delete()
      .in("id", notificationIds);
    return !error;
  }

  async deleteAllNotifications() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return 0;

      let query = supabase.from("notifications").delete();

      const userRole = await this._getUserRole(user);

      if (userRole === "student") {
        const { data: studentData } = await supabase
          .from("users_unified")
          .select("id")
          .eq("email", user.email)
          .eq("role", "student")
          .maybeSingle();
        if (studentData) {
          query = query.eq("student_id", studentData.id).is("user_id", null);
        } else return 0;
      } else {
        query = query.eq("user_id", user.id);
      }

      const { error, count } = await query.select("*", { count: "exact" });

      if (error) {
        logger.error("🔔 Error in deleteAllNotifications:", error);
        throw error;
      }

      return count || 0;
    } catch (error) {
      logger.error("🔔 Catch in deleteAllNotifications:", error);
      return 0;
    }
  }

  async getRecentNotifications(limit = 5) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];

      let query = supabase
        .from("notifications")
        .select("*")
        .order("created_at", { ascending: false });

      const userRole = user.app_metadata?.role || user.role;

      if (userRole === "student") {
        const { data: studentData } = await supabase
          .from("users_unified")
          .select("id")
          .eq("email", user.email)
          .eq("role", "student")
          .maybeSingle();
        if (studentData) {
          query = query.eq("student_id", studentData.id).is("user_id", null);
        } else return [];
      } else {
        query = query.eq("user_id", user.id);
      }

      const { data, error } = await query.limit(limit);

      if (error) return [];

      // Фильтруем по предпочтениям пользователя (скрываем выключенные типы)
      const disabledTypes = await this._getDisabledPrefTypes(user.id);
      const filteredData = this._filterByPrefs(data, disabledTypes);

      const enriched = await Promise.all(
        (filteredData || []).map(async (n) => {
          if (n.student_id) {
            const { data: student } = await supabase
              .from("users_unified")
              .select("full_name, class")
              .eq("id", n.student_id)
              .maybeSingle();
            if (student) {
              n.student_name = student.full_name;
              n.student_class = student.class;
            }
          }
          return n;
        }),
      );

      return enriched;
    } catch (error) {
      return [];
    }
  }

  async getNotificationStats() {
    const unread = await this.getUnreadCount();
    return {
      total: "?",
      unread: unread,
      read: "?",
      by_type: {},
    };
  }

  async createOrderCreatedNotification(order, student) {
    try {
      const notifications = [];

      // 1. Уведомление для студента/родителя (user_id = null)
      notifications.push({
        type: "order_created",
        title: "✨ Заказ размещен",
        message: `Новый заказ для ученика ${student.full_name} на сумму ${order.total_price} ₽`,
        student_id: student.id,
        user_id: null,
        order_id: order.id,
      });

      // 2. Уведомление для тьютора
      if (student.class) {
        const tutor = await this._getTutorByClass(student.class);
        if (tutor) {
          notifications.push({
            type: "order_created",
            title: `✨ Новый заказ (${student.class})`,
            message: `В вашем классе новый заказ: ${student.full_name} на ${order.total_price} ₽`,
            student_id: student.id,
            user_id: tutor.id,
            order_id: order.id,
          });
        }
      }

      // 3. Уведомление для админов (опционально, если нужно в ленту)
      const admins = await this._getSystemAdmins();
      (admins || []).forEach((admin) => {
        notifications.push({
          type: "order_created",
          title: "✨ Поступил заказ",
          message: `Класс ${student.class}: ${student.full_name} (${order.total_price} ₽)`,
          student_id: student.id,
          user_id: admin.id,
          order_id: order.id,
        });
      });

      const { error } = await supabase
        .from("notifications")
        .insert(notifications);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      // Single log entry - removed double-logging
      logger.error("🔔 Order notification creation failed", {
        orderId: order?.id,
        studentId: student?.id,
        error: error?.message,
        code: error?.code,
      });
      return { success: false, error };
    }
  }

  /**
   * Уведомление об изменении заказа админом/тьютором
   */
  async notifyOrderEdited(order, editorUser) {
    try {
      // --- Guard: уведомляем только если изменение касается СЕГОДНЯШНЕГО дня
      // ТЕКУЩЕЙ недели. ---
      // Заказ недельный (order_json содержит ВСЕ дни), поэтому наличие today в
      // order_json НЕ доказывает, что изменился именно сегодня. Точный сигнал —
      // prev_order_diff (ключи = реально изменённые дни). Для INSERT/DELETE
      // (нет prev_order_diff) откатываемся к наличию дня в заказе.
      // ВАЖНО: день недели должен совпадать И неделя заказа должна быть текущей,
      // иначе изменение заказа на СЛЕДУЮЩУЮ неделю с тем же днём (напр. пятница
      // следующей недели) ошибочно совпадёт с todayDayKey.
      const now = new Date();
      const todayDayKey = now.toLocaleDateString("en-US", {
        weekday: "long",
      });

      // Понедельник текущей недели (YYYY-MM-DD), как menu_week_start.
      const nowDay = now.getDay(); // 0=Sun..6=Sat
      const diffToMonday = nowDay === 0 ? 6 : nowDay - 1;
      const monday = new Date(now);
      monday.setDate(now.getDate() - diffToMonday);
      const mondayStr = monday.toISOString().slice(0, 10);
      const weekStartStr = order?.menu_week_start
        ? String(order.menu_week_start).slice(0, 10)
        : null;
      const isCurrentWeek = weekStartStr === mondayStr;

      const prevDiff = order?.prev_order_diff;
      const diffKeys =
        prevDiff && typeof prevDiff === "object" ? Object.keys(prevDiff) : [];

      // Для UPDATE: слать ТОЛЬКО если реально изменились блюда (diffKeys непустой)
      // И среди изменённых дней есть сегодняшний. Пустой prev_order_diff = заказ
      // пересохранён без изменения блюд (напр. после дедлайна) — НЕ слать.
      // Для INSERT (нет prev_order_diff) — слать если заказ содержит сегодня.
      const isNewOrder = !order?.prev_order_json;
      const todayChanged = isNewOrder
        ? Boolean(
            order?.order_json &&
              typeof order.order_json === "object" &&
              order.order_json[todayDayKey],
          )
        : diffKeys.length > 0 && diffKeys.includes(todayDayKey);

      if (!todayChanged || !isCurrentWeek) {
        // Изменение не касается сегодняшнего дня текущей недели — не шлём.
        return {
          success: true,
          skipped: true,
          reason: "not today's menu day",
          day: todayDayKey,
          week_start: weekStartStr,
          current_week: mondayStr,
        };
      }

      const { data: student } = await supabase
        .from("users_unified")
        .select("full_name, class")
        .eq("id", order.student_id)
        .maybeSingle();

      const { data: editor } = await supabase
        .from("users_unified")
        .select("full_name")
        .eq("id", editorUser.id)
        .maybeSingle();

      const editorName =
        editor?.full_name || editorUser.email || "Администратор";
      const studentName = student?.full_name || "Ученик";

      const notifications = [];

      // 1. Студенту/Родителю
      notifications.push({
        type: "order_edited",
        title: "📝 Заказ изменен",
        message: `Ваш заказ для ${studentName} был изменен пользователем ${editorName}`,
        student_id: order.student_id,
        order_id: order.id,
        user_id: null,
      });

      // 2. Тьютору (если это не сам тьютор редактирует)
      if (student?.class) {
        const tutor = await this._getTutorByClass(student.class);
        if (tutor && tutor.id !== editorUser.id) {
          notifications.push({
            type: "order_edited",
            title: `📝 Изменение заказа (${student?.class || ""})`,
            message: `Заказ ученика ${studentName} был отредактирован (${editorName})`,
            student_id: order.student_id,
            order_id: order.id,
            user_id: tutor.id,
          });
        }
      }

      await supabase.from("notifications").insert(notifications);
    } catch (e) {
      logger.error("🔔 Error notifyOrderEdited:", e);
    }
  }

  /**
   * Уведомление о пополнении баланса
   */
  async notifyPaymentSuccess(studentId, amount) {
    try {
      const { data: student } = await supabase
        .from("users_unified")
        .select("full_name")
        .eq("id", studentId)
        .maybeSingle();

      await supabase.from("notifications").insert([
        {
          type: "payment_added",
          title: "💰 Баланс пополнен",
          message: `На счет ${student?.full_name || "ученика"} зачислено ${amount} ₽. Приятного аппетита!`,
          student_id: studentId,
          user_id: null,
        },
      ]);
    } catch (e) {
      logger.error("🔔 Error notifyPaymentSuccess:", e);
    }
  }

  async createOrderEditNotification(order, user) {
    try {
      const notifications = [];

      const { data: student } = await supabase
        .from("users_unified")
        .select("id, full_name, class")
        .eq("id", order.student_id)
        .maybeSingle();

      if (!student) return { success: false };

      const { data: editor } = await supabase
        .from("users_unified")
        .select("full_name")
        .eq("id", user.id)
        .maybeSingle();

      const editorName = editor?.full_name || user.email || "Пользователь";

      // 1. Для студента
      if (user.id !== student.id) {
        notifications.push({
          type: "order_edited",
          title: "📝 Ваш заказ изменен",
          message: `Заказ отредактирован пользователем ${editorName}`,
          student_id: student.id,
          user_id: null,
          order_id: order.id,
        });
      }

      // 2. Для админов - ОТКЛЮЧЕНО (переход на Telegram)
      /*
      const { data: admins } = await supabase.rpc("get_system_admins");

      (admins || []).forEach((admin) => {
        notifications.push({
          type: "order_edited",
          title: "📝 Заказ отредактирован",
          message: `${student.full_name} (${student.class}): заказ изменен на ${order.total_price} ₽. Редактор: ${editorName}`,
          student_id: student.id,
          user_id: admin.id,
          order_id: order.id,
        });
      });
      */

      // 3. Для тьютора - ОТКЛЮЧЕНО (переход на Telegram)
      /*
      const { data: tutor } = await supabase
        .rpc("get_class_tutor", { class_name: student.class })
        .maybeSingle();

      if (tutor && tutor.id !== user.id) {
        notifications.push({
          type: "order_edited",
          title: "📝 Заказ изменен в вашем классе",
          message: `${student.full_name}: заказ изменен на ${order.total_price} ₽. Редактор: ${editorName}`,
          student_id: student.id,
          user_id: tutor.id,
          order_id: order.id,
        });
      }
      */

      if (notifications.length > 0) {
        const { error } = await supabase
          .from("notifications")
          .insert(notifications);
        if (error) throw error;
      }

      return { success: true };
    } catch (error) {
      logger.error("Error creating order edit notifications:", error);
      return { success: false, error };
    }
  }

  async createPaymentNotification(student, amount, method, description) {
    const methodNames = {
      transfer: "банковский перевод",
      cash: "наличные",
      card: "карта",
      telegram: "Telegram",
    };
    const methodName = methodNames[method] || method;

    return await supabase.from("notifications").insert([
      {
        type: "payment_added",
        title: "💰 Пополнение баланса",
        message: `Ваш баланс пополнен на ${amount} ₽ (${methodName}). ${description ? `Комментарий: ${description}` : ""}`,
        student_id: student.id,
        user_id: null,
      },
    ]);
  }

  async createMenuActivationNotification(menu) {
    try {
      const { data: users } = await supabase
        .from("users_unified")
        .select("id, role")
        .in("role", ["student", "tutor"]);

      if (!users || users.length === 0) return { success: true };

      const notifications = users.map((user) => ({
        type: "menu_activated",
        title: "📅 Новое меню доступно",
        message: `Доступно новое меню на период ${new Date(menu.week_start).toLocaleDateString("ru-RU")} — ${new Date(menu.week_end).toLocaleDateString("ru-RU")}. Сделайте заказ!`,
        student_id: user.role === "student" ? user.id : null,
        user_id: user.role === "tutor" ? user.id : null,
      }));

      const { error } = await supabase
        .from("notifications")
        .insert(notifications);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      logger.error("Error creating menu activation notifications:", error);
      return { success: false, error };
    }
  }

  formatNotification(notification) {
    const formatted = { ...notification };
    formatted.formatted_date = new Date(notification.created_at).toLocaleString(
      "ru-RU",
    );
    formatted.created_at_formatted = formatted.formatted_date;

    const configs = {
      order_created: { icon: "✨", color: "green" },
      order_edited: { icon: "📝", color: "blue" },
      order_cancelled: { icon: "❌", color: "red" },
      payment_added: { icon: "💰", color: "green" },
      menu_activated: { icon: "📅", color: "purple" },
    };

    const config = configs[notification.type] || { icon: "📢", color: "gray" };
    formatted.icon = config.icon;
    formatted.color = config.color;

    return formatted;
  }
}

export const notificationService = new NotificationService();
export default notificationService;
